import React, { useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid';
import { Button, Card, CardContent, List, ListItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormContext, useFieldArray } from 'react-hook-form';
import Input from 'components/Common/Input';
import Add from '@mui/icons-material/Add';
import PhoneNumberInput from 'components/Common/PhoneNumberInput';
import { PatientInvite } from 'pages/Dashboard/types';
import PatientInviteAlert from 'pages/Dashboard/components/PatientInviteAlert';
import { PatientContext } from 'pages/Dashboard/AddPatientContext';
import numOnly from 'pages/Dashboard/utils/addNewPatientUtils';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';

const DELETE_ICON_BREAKPOINT = 0.5;
const MD_BREAKPOINT = (12 - DELETE_ICON_BREAKPOINT) / 3;

export default function PatientInviteForm() {
  const { state, addingPatient } = React.useContext(PatientContext);
  const { patientInviteErrorsMap } = state;

  const { control, watch, formState } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'patients',
    shouldUnregister: true,
  });

  const { isSubmitted } = formState;

  const providerId = watch('providerId');

  const listItemRefs = useRef<(HTMLLIElement | null)[]>([]);

  const getContactFields = (index: number, mobile = '') => {
    const fieldErrors = patientInviteErrorsMap[mobile || numOnly(watch(`patients.${index}.mobile`))];
    return fieldErrors?.contactFields;
  };

  const getFieldError = (index: number) => {
    const formattedMobile = numOnly(watch(`patients.${index}.mobile`));
    const email = watch(`patients.${index}.email`);
    getContactFields(index, formattedMobile);
    const fieldErrors = patientInviteErrorsMap[formattedMobile];
    const fieldError = fieldErrors?.contactFields?.[formattedMobile]
      || fieldErrors?.contactFields?.[email];
    if (fieldError) {
      const hasSameProviderId = fieldError.patient?.provider?.id === providerId;
      return {
        patient: fieldError.patient,
        onboarded: fieldError.onboarded,
        phoneExists: !!fieldErrors?.contactFields?.[formattedMobile]?.patient?.id,
        hasError: true,
        hasSameProviderId,
      };
    }

    return {
      patient: {},
      onboarded: false,
      phoneExists: false,
      hasError: false,
      hasSameProviderId: false,
    };
  };

  const fieldCallbackFn = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
    else {
      remove(0);
      append({ mobile: '', email: '' });
    }
  };

  // Scroll to first error after submit
  useEffect(() => {
    if (formState.isSubmitted && !addingPatient) {
      const firstErrorIndex = fields.findIndex((_, idx) => !!getContactFields(idx));
      if (firstErrorIndex !== -1 && listItemRefs.current[firstErrorIndex]) {
        listItemRefs.current[firstErrorIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isSubmitted, fields.length, addingPatient]);

  return (
    <>
      <Grid container>
        <Grid item md={12}>
          <List>
            {fields.map((item, index) => (
              <Card sx={{ mb: 1 }} key={item.id}>
                <CardContent>
                  <ListItem sx={{ p: 0 }} ref={(el) => { listItemRefs.current[index] = el; }}>
                    <Grid container spacing={1.5}>
                      <Grid item md={MD_BREAKPOINT} xs={MD_BREAKPOINT * 3} className='fs-mask'>
                        <Input
                          control={control}
                          name={`patients.${index}.firstName`}
                          label='First name'
                          textFieldProps={{
                            type: 'text',
                          }}
                          rules={{
                            validate: (val: string) => !!val || 'First name is required',
                          }}
                        />
                      </Grid>
                      <Grid item md={MD_BREAKPOINT} xs={MD_BREAKPOINT * 3} className='fs-mask'>
                        <Input
                          control={control}
                          name={`patients.${index}.lastName`}
                          label='Last name'
                          textFieldProps={{
                            type: 'text',
                          }}
                          rules={{
                            validate: (val: string) => !!val || 'Last name is required',
                          }}
                        />
                      </Grid>
                      <Grid item md={MD_BREAKPOINT} xs={MD_BREAKPOINT * 3} className='fs-mask'>
                        <Input
                          control={control}
                          name={`patients.${index}.preferredName`}
                          label='Preferred name (optional)'
                          textFieldProps={{
                            type: 'text',
                          }}
                        />
                      </Grid>
                      {fields.length > 1 && (
                        <Grid
                          item
                          xs={DELETE_ICON_BREAKPOINT}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <CloseIcon
                            onClick={() => remove(index)}
                            sx={{ color: '#0000008A', cursor: 'pointer' }}
                          />
                        </Grid>
                      )}
                      <Grid item md={MD_BREAKPOINT} xs={MD_BREAKPOINT * 3} className='fs-mask'>
                        <PhoneNumberInput
                          name={`patients.${index}.mobile`}
                          control={control}
                          label='Mobile number'
                          rules={{
                            validate: (val: string) => {
                              if (!val) {
                                return 'Mobile number is required';
                              }

                              const phoneNumberPattern = /^\((?!1|0)\d{3}\)\d{3}-\d{4}$/;
                              if (!phoneNumberPattern.test(val)) {
                                return 'Invalid phone number';
                              }

                              const patients: PatientInvite[] = watch('patients');
                              const duplicateIndexes = patients.reduce(
                                (indicesArr, patient, idx) => {
                                  if (patient.mobile && patient.mobile === val) {
                                    return [...indicesArr, idx];
                                  }
                                  return [...indicesArr];
                                },
                                [] as Array<number>,
                              );
                              if (duplicateIndexes.length < 2) {
                                return true;
                              }
                              if (duplicateIndexes[duplicateIndexes.length - 1] === index) {
                                return 'Duplicate mobile number';
                              }
                              return true;
                            },
                          }}
                          textStyles={{
                            '& .MuiFormHelperText-root': {
                              marginX: 0,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item md={MD_BREAKPOINT * 2} xs={MD_BREAKPOINT * 3} className='fs-mask'>
                        <Input
                          control={control}
                          name={`patients.${index}.email`}
                          label='Email address (optional)'
                          textFieldProps={{
                            type: 'email',
                          }}
                          rules={{
                            validate: (val) => {
                              const patients: PatientInvite[] = watch('patients');
                              const duplicateIndexes = patients.reduce(
                                (indicesArr, patient, idx) => {
                                  if (patient.email && patient.email === val) {
                                    return [...indicesArr, idx];
                                  }
                                  return [...indicesArr];
                                },
                                [] as Array<number>,
                              );
                              if (duplicateIndexes.length < 2) {
                                return true;
                              }
                              if (duplicateIndexes[duplicateIndexes.length - 1] === index) {
                                return 'Duplicate email address';
                              }
                              return true;
                            },
                          }}
                        />
                      </Grid>
                      {isSubmitted && !addingPatient && (() => {
                        const { hasError,
                          patient,
                          phoneExists,
                          onboarded,
                          hasSameProviderId,
                        } = getFieldError(index);
                        return hasError && (
                        <Grid item xs={MD_BREAKPOINT * 3}>
                          <PatientInviteAlert
                            isDemographicFlow={false}
                            patient={patient}
                            hasExistingPhoneNumber={phoneExists}
                            isOnboarded={onboarded}
                            callbackFn={() => fieldCallbackFn(index)}
                            hasSameProviderId={hasSameProviderId}
                          />
                        </Grid>
                        );
                      })()}
                    </Grid>
                  </ListItem>
                </CardContent>
              </Card>
            ))}
          </List>
        </Grid>
      </Grid>
      <Button
        variant='outlined'
        sx={{ width: { xs: '100%', md: 'fit-content' }, height: { xs: '20px', md: '40px' } }}
        startIcon={<Add />}
        onClick={() => {
          trackMixpanelEvent(MixpanelEventName.ADD_PATIENT_ADD_ANOTHER_BUTTON_CLICKED);
          append({ mobile: '', email: '' });
        }}
        disabled={fields.length === 10}
      >
        ADD ANOTHER
      </Button>
    </>
  );
}
